import React, { useState } from 'react';
import { IconCopy, IconCheck } from '@tabler/icons-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Props for the CodeBlock component
 */
interface CodeBlockProps {
  /** The code content to display */
  children: string;
  /** The programming language for syntax highlighting */
  language?: string;
  /** Additional CSS class names */
  className?: string;
}

/**
 * CodeBlock component for displaying syntax-highlighted code with copy functionality
 *
 * @description
 * A feature-rich code block component that displays syntax-highlighted code
 * with a copy-to-clipboard button. Uses react-syntax-highlighter with Prism
 * for syntax highlighting. The syntax highlighter is lazy-loaded to improve
 * initial page load performance.
 *
 * Features:
 * - Syntax highlighting for multiple languages (lazy loaded)
 * - Copy to clipboard functionality
 * - Visual feedback on copy (icon changes for 2 seconds)
 * - Dark theme optimized styling
 * - Language badge display
 * - Responsive design
 * - Loading skeleton while syntax highlighter loads
 * - Fallback to plain <pre> if lazy loading fails
 *
 * Performance:
 * - Uses React.lazy() to code-split the syntax highlighter
 * - Reduces initial bundle size
 * - Shows loading skeleton during load
 *
 * @example
 * ```tsx
 * <CodeBlock language="typescript">
 *   const hello = "world";
 * </CodeBlock>
 * ```
 */
export function CodeBlock({ children, language, className }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  /**
   * Handles copying code to clipboard
   */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch {
      // Silently fail - clipboard API may not be available
    }
  };

  return (
    <div className={cn('relative group', className)}>
      {/* Language badge and copy button */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#282c34] border-b border-gray-700">
        {language && (
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
            {language}
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="h-8 px-2 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          aria-label={copied ? 'Copied' : 'Copy code'}
        >
          {copied ? (
            <>
              <IconCheck size={16} className="mr-1" />
              <span className="text-xs">Copied!</span>
            </>
          ) : (
            <>
              <IconCopy size={16} className="mr-1" />
              <span className="text-xs">Copy</span>
            </>
          )}
        </Button>
      </div>

      {/* Code content with syntax highlighting */}
      <div className="overflow-x-auto">
        <SyntaxHighlighter
          language={language || 'text'}
          style={oneDark}
          customStyle={{
            margin: 0,
            padding: '1rem',
            fontSize: '0.875rem',
            lineHeight: '1.5',
            borderRadius: 0,
            backgroundColor: '#282c34',
          }}
          showLineNumbers
          wrapLines
        >
          {children}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}
